import React, { useCallback } from 'react';
import { IMaskInput } from 'react-imask';
import styled from 'styled-components';
import { toRem } from 'design';
import { useDebounce } from 'hooks';
import { Duration } from 'utils/duration';
import InputStyles from './InputStyles';

interface IStyledInputProps {
  width?: number;
  height?: number;
}

export interface IDurationResponse {
  minutes?: number;
  error?: Error;
  isSuccess: boolean;
}

interface IDurationInputProps extends IStyledInputProps {
  dataCy?: string;
  defaultValue?: number;
  id?: string;
  className?: string;
  disabled?: boolean;
  onChange?: (res: IDurationResponse) => void;
}

const Layout = styled.div<{ width?: number }>`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: ${({ width }) => (width ? toRem(width) : '100%')};
  height: auto;

  input {
    padding-right: ${toRem(35.75)};
  }

  /**
   * Remove arrows from number input
   */
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledInput = styled(IMaskInput)<IStyledInputProps>`
  ${InputStyles};

  font-size: ${toRem(14)};

  ${({ height }) => height && `height: ${toRem(height)};`}
  ${({ width }) => width && `width: ${toRem(width)};`}
`;

const masks = [
  {
    mask: '1:23',
    definitions: {
      1: /[1-9]/,
      2: /[0-5]/,
      3: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /0/,
      2: /0/,
      3: /[1-5]/,
      4: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /0/,
      2: /0/,
      3: /0/,
      4: /[1-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /0/,
      2: /[1-9]/,
      3: /[0-5]/,
      4: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /1/,
      2: /[0-9]/,
      3: /[0-5]/,
      4: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /2/,
      2: /[0-3]/,
      3: /[0-5]/,
      4: /[0-9]/,
    },
  },
  {
    mask: '12:34',
    definitions: {
      1: /2/,
      2: /4/,
      3: /0/,
      4: /0/,
    },
  },
];

export const DurationInput: React.FC<IDurationInputProps> = ({ dataCy = 'duration-input', defaultValue, disabled, width, onChange, ...rest }) => {
  const onDurationInputChange = useCallback(
    (val: string) => {
      if (!onChange) return;
      try {
        const value = val ? Duration.convertDurationStringToMins(val) : 0;
        onChange({ minutes: value, isSuccess: true });
      } catch (err: any) {
        onChange({ error: err, isSuccess: false });
      }
    },
    [onChange]
  );
  const debouncedOnDurationChange = useDebounce(onDurationInputChange, 500);

  return (
    <Layout width={width} data-cy={dataCy}>
      <StyledInput
        placeholder="00:00"
        defaultValue={defaultValue ? new Duration(defaultValue).toString() : ''}
        mask={masks}
        lazy={false}
        onAccept={debouncedOnDurationChange}
        disabled={disabled}
        readOnly={false}
        type="text"
        inputMode="numeric"
        maxLength={5}
        {...rest}
      />
    </Layout>
  );
};
